import React, { useEffect, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import CompanyApi from '../../../../api/data-sections/companyApi';
import StandardDataGrid from '../../../../components/Common/StandardDataGrid';
import { useSystemError } from '../../../../components/Error/SystemErrorContext';
import { CompanyProfileModel } from '../../../../models/data-sections/company/profileModel';
import { CompanyFinancialsModel } from '../../../../models/data-sections/company/financialsModel';
import { ResponseErrorModel } from '../../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../../utils/errorUtils';

interface CompanyModalProps {
  selected_company: CompanyProfileModel;
  searchExecutionId: string;
  onClose: () => void;
}

const CompanyModal: React.FC<CompanyModalProps> = ({
  selected_company,
  searchExecutionId,
}) => {
  const [companyProfile, setCompanyProfile] = useState<CompanyProfileModel | null>(null);
  const [financials, setFinancials] = useState<CompanyFinancialsModel[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [didErrorOccur, setDidErrorOccur] = useState(false);

  const { triggerSystemError } = useSystemError();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await CompanyApi.getCompanyProfileFinancials(searchExecutionId, selected_company.company_key);

        if (response.success) {
          const data = (response as ResponseSuccessModel).data as {
            profile: CompanyProfileModel[];
            financials: CompanyFinancialsModel[];
          }[];

          if (data && data.length > 0) {
            setFinancials(data[0].financials);
            setCompanyProfile(data[0].profile[0]);
          }
        } else {
          handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
        }
      } catch (error) {
        handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [searchExecutionId, selected_company?.company_key]);

  const incomeColumns: GridColDef[] = [
    { field: 'fiscal_date_ending', headerName: 'Fiscal Year', width: 150 },
    { field: 'total_revenue', headerName: 'Total Revenue', width: 180 },
    { field: 'gross_profit', headerName: 'Gross Profit', width: 180 },
    { field: 'net_income', headerName: 'Net Income', width: 180 },
    { field: 'ebitda', headerName: 'EBITDA', width: 180 },
  ];

  const earningsAnnualColumns: GridColDef[] = [
    { field: 'fiscal_date_ending', headerName: 'Fiscal Year', width: 130 },
    { field: 'reported_eps', headerName: 'Reported EPS', width: 160 }
  ];

  const earningsQuarterlyColumns: GridColDef[] = [
    { field: 'fiscal_date_ending', headerName: 'Fiscal Quarter', width: 130 },
    { field: 'reported_eps', headerName: 'Reported EPS', width: 160 },
    { field: 'estimated_eps', headerName: 'Estimated EPS', width: 160 },
    { field: 'surprise', headerName: 'Surprise', width: 120 },
    { field: 'surprise_percentage', headerName: 'Surprise %', width: 140 }
  ];

  // filtered data
  const incomeAnnual = financials?.filter(
    (f) => f.source_type === 'income_statement' && f.frequency === 'annual'
  ) || [];

  const incomeQuarterly = financials?.filter(
    (f) => f.source_type === 'income_statement' && f.frequency === 'quarterly'
  ) || [];

  const earningsAnnual = financials?.filter(
    (f) => f.source_type === 'earnings' && f.frequency === 'annual'
  ) || [];

  const earningsQuarterly = financials?.filter(
    (f) => f.source_type === 'earnings' && f.frequency === 'quarterly'
  ) || [];

  const overviewComponent = (
    <Box p={2}>

  <Typography variant="h6" color="primary.dark">General</Typography>
  <Divider sx={{ mb: 2 }} />
  <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }} gap={2} mb={3}>
    <Typography><strong>Name:</strong> {companyProfile?.company_name || 'N/A'}</Typography>
    <Typography><strong>Ticker:</strong> {companyProfile?.ticker || 'N/A'}</Typography>
    <Typography><strong>Sector:</strong> {companyProfile?.sector || 'N/A'}</Typography>
    <Typography><strong>Industry:</strong> {companyProfile?.industry || 'N/A'}</Typography>
    <Typography><strong>Country:</strong> {companyProfile?.country || 'N/A'}</Typography>
    <Typography><strong>Asset Type:</strong> {companyProfile?.asset_type || 'N/A'}</Typography>
    {companyProfile?.official_site && (
      <Typography>
        <strong>Website:</strong>{' '}
        <a
          href={companyProfile?.official_site}
          target="_blank"
          rel="noopener noreferrer"
        >
          {companyProfile?.official_site}
        </a>
      </Typography>
    )}
  </Box>

  <Typography variant="h6" color="primary.dark" sx={{ mt: 5 }}>Financials</Typography>
  <Divider sx={{ mb: 2 }} />
  <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }} gap={2} mb={3}>
    <Typography><strong>Market Cap:</strong> {companyProfile?.market_capitalization || 'N/A'}</Typography>
    <Typography><strong>Price:</strong> {companyProfile?.price ?? 'N/A'}</Typography>
    <Typography><strong>PE Ratio:</strong> {companyProfile?.pe_ratio || 'N/A'}</Typography>
    <Typography><strong>EPS:</strong> {companyProfile?.eps || 'N/A'}</Typography>
    <Typography><strong>Dividend Yield:</strong> {companyProfile?.dividend_yield || 'N/A'}</Typography>
    <Typography><strong>Volume:</strong> {companyProfile?.volume ?? 'N/A'}</Typography>
    <Typography><strong>Book Value:</strong> {companyProfile?.book_value || 'N/A'}</Typography>
    <Typography><strong>Beta:</strong> {companyProfile?.beta || 'N/A'}</Typography>
    <Typography><strong>Price to Book:</strong> {companyProfile?.price_to_book_ratio || 'N/A'}</Typography>
  </Box>

  <Typography variant="h6" color="primary.dark" sx={{ mt: 5 }}>Analyst Ratings</Typography>
  <Divider sx={{ mb: 2 }} />
  <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }} gap={2} mb={3}>
    <Typography><strong>Buy:</strong> {companyProfile?.analyst_rating_buy || 'N/A'}</Typography>
    <Typography><strong>Hold:</strong> {companyProfile?.analyst_rating_hold || 'N/A'}</Typography>
    <Typography><strong>Sell:</strong> {companyProfile?.analyst_rating_sell || 'N/A'}</Typography>
    <Typography><strong>Strong Buy:</strong> {companyProfile?.analyst_rating_strong_buy || 'N/A'}</Typography>
    <Typography><strong>Strong Sell:</strong> {companyProfile?.analyst_rating_strong_sell || 'N/A'}</Typography>
    <Typography><strong>Target Price:</strong> {companyProfile?.analyst_target_price || 'N/A'}</Typography>
  </Box>

  <Typography variant="h6" color="primary.dark" sx={{ mt: 5 }}>Trading Metrics</Typography>
  <Divider sx={{ mb: 2 }} />
  <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }} gap={2}>
    <Typography><strong>52 Week High:</strong> {companyProfile?.week_52_high || 'N/A'}</Typography>
    <Typography><strong>52 Week Low:</strong> {companyProfile?.week_52_low || 'N/A'}</Typography>
    <Typography><strong>50 Day MA:</strong> {companyProfile?.day_50_moving_average || 'N/A'}</Typography>
    <Typography><strong>200 Day MA:</strong> {companyProfile?.day_200_moving_average || 'N/A'}</Typography>
    <Typography><strong>Previous Close:</strong> {companyProfile?.previous_close ?? 'N/A'}</Typography>
    <Typography><strong>Open:</strong> {companyProfile?.open ?? 'N/A'}</Typography>
  </Box>

</Box>

  );

  const tabs = [
    {
      label: 'Overview',
      component: overviewComponent,
    },
    {
      label: 'Income (Annual)',
      rows: incomeAnnual.map((row) => ({
        id: row.fiscal_date_ending,
        ...row,
        fiscal_date_ending: row.fiscal_date_ending
          ? new Date(row.fiscal_date_ending).getFullYear().toString()
          : 'N/A',
        total_revenue: row.total_revenue
          ? `$${row.total_revenue.toLocaleString()}`
          : 'N/A',
        gross_profit: row.gross_profit
          ? `$${row.gross_profit.toLocaleString()}`
          : 'N/A',
        net_income: row.net_income
          ? `$${row.net_income.toLocaleString()}`
          : 'N/A',
        ebitda: row.ebitda
          ? `$${row.ebitda.toLocaleString()}`
          : 'N/A',
      })),
      columns: incomeColumns,
      modalRenderer: () => null,
    },
    {
      label: 'Income (Quarterly)',
      rows: incomeQuarterly.map((row, i) => ({
        id: `${row.fiscal_date_ending}-${i}`,
        ...row,
        fiscal_date_ending: row.fiscal_date_ending
          ? new Date(row.fiscal_date_ending).toISOString().slice(0, 7)
          : 'N/A',
        total_revenue: row.total_revenue
          ? `$${row.total_revenue.toLocaleString()}`
          : 'N/A',
        gross_profit: row.gross_profit
          ? `$${row.gross_profit.toLocaleString()}`
          : 'N/A',
        net_income: row.net_income
          ? `$${row.net_income.toLocaleString()}`
          : 'N/A',
        ebitda: row.ebitda
          ? `$${row.ebitda.toLocaleString()}`
          : 'N/A',
      })),
      columns: [
        { ...incomeColumns[0], headerName: 'Fiscal Quarter' },
        ...incomeColumns.slice(1)
      ],
      modalRenderer: () => null,
    },
    {
      label: 'Earnings (Annual)',
      rows: earningsAnnual.map((row) => ({
        id: row.fiscal_date_ending,
        ...row,
        fiscal_date_ending: row.fiscal_date_ending
          ? new Date(row.fiscal_date_ending).getFullYear().toString()
          : 'N/A',
        reported_eps: row.reported_eps ?? 'N/A',
      })),
      columns: earningsAnnualColumns,
      modalRenderer: () => null,
    },
    {
      label: 'Earnings (Quarterly)',
      rows: earningsQuarterly.map((row, i) => ({
        id: `${row.fiscal_date_ending}-${i}`,
        ...row,
        fiscal_date_ending: row.fiscal_date_ending
          ? new Date(row.fiscal_date_ending).toISOString().slice(0, 7)
          : 'N/A',
        reported_eps: row.reported_eps ?? 'N/A',
        estimated_eps: row.estimated_eps ?? 'N/A',
        surprise: row.surprise ?? 'N/A',
        surprise_percentage: row.surprise_percentage ?? 'N/A',
      })),
      columns: earningsQuarterlyColumns,
      modalRenderer: () => null,
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 150px)', width: '100%' }}>
      <StandardDataGrid isLoading={isLoading} tabs={tabs} />
    </Box>
  );
};

export default CompanyModal;
